import {useEffect, useState} from 'react';

import {useAppSelector} from '../../../helpers/reduxHelpers';
import {getAnalyticsSelectedMetrics} from '../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {AnyGraphMetricsEnum} from '../../components/reportElements/helperTypes';
import {ReportElementIdsEnum} from '../analyticsReportHelpers';
import {getMetricLabels, MetricWithLabel} from '../metricLabels';

/*
 * Types.
 */

type SelectedMetricResult = [AnyGraphMetricsEnum | undefined, Partial<MetricWithLabel<AnyGraphMetricsEnum>>];

/*
 * Hook.
 */

/**
 * Custom hook to retrieve the selected metric (and it's associated label and tooltip)
 * and dispatch updates
 */
export function useSelectedMetric(
  reportSectionId: ReportElementIdsEnum,
  availableMetrics: ReadonlyArray<AnyGraphMetricsEnum>,
): SelectedMetricResult {
  const selectedMetrics = useAppSelector(getAnalyticsSelectedMetrics);

  const defaultSelectedMetric = availableMetrics.length > 0 ? availableMetrics[0] : undefined;

  const [selectedMetric, setSelectedMetric] = useState(defaultSelectedMetric);

  // This will trigger when we select a view or change the selected metric.
  useEffect(() => {
    if (selectedMetrics[reportSectionId] && selectedMetrics[reportSectionId][0]) {
      setSelectedMetric(selectedMetrics[reportSectionId][0] as AnyGraphMetricsEnum);
    } else {
      setSelectedMetric(defaultSelectedMetric);
    } // reset to original value.
  }, [defaultSelectedMetric, reportSectionId, selectedMetrics]);

  const labels = getMetricLabels<AnyGraphMetricsEnum>(selectedMetric);

  return [selectedMetric, labels];
}
